import React, { useState } from 'react';
import { FileSpreadsheet, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import api from '../../utils/api';
import { toast } from 'sonner';

const emptyManual = { transaction_date: new Date().toISOString().slice(0, 10), direction: 'expense', amount: '', description: '', counterparty_name: '', counterparty_iban: '', reference: '' };

export const AccountingImportDialog = ({ open, mode = 'file', onOpenChange, companyId, accountId, fiscalYear, onDone }) => {
  const [tab, setTab] = useState(mode);
  const [file, setFile] = useState(null);
  const [manual, setManual] = useState(emptyManual);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { if (open) setTab(mode); }, [open, mode]);
  React.useEffect(() => {
    if (open && fiscalYear && !String(manual.transaction_date || '').startsWith(String(fiscalYear))) {
      setManual((prev) => ({ ...prev, transaction_date: `${fiscalYear}-01-01` }));
    }
  }, [open, fiscalYear, manual.transaction_date]);

  const upload = async () => {
    if (!file) return toast.error('Seleziona un file CSV, XLS, XLSX, XML o PDF PayPal');
    const data = new FormData();
    data.append('company_id', companyId); data.append('bank_account_id', accountId); data.append('fiscal_year', String(fiscalYear)); data.append('file', file);
    setSaving(true);
    try {
      const res = await api.post('/accounting-control/transactions/import', data, { timeout: 120000 });
      toast.success(res.data.message || 'Estratto conto importato');
      onDone(); onOpenChange(false); setFile(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Importazione non riuscita'); }
    finally { setSaving(false); }
  };

  const createManual = async () => {
    setSaving(true);
    try {
      await api.post('/accounting-control/transactions/manual', { ...manual, company_id: companyId, bank_account_id: accountId, amount: Number(manual.amount) });
      toast.success('Movimento registrato'); onDone(); onOpenChange(false); setManual(emptyManual);
    } catch (error) { toast.error(error.response?.data?.detail || 'Salvataggio non riuscito'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="accounting-import-dialog">
        <DialogHeader>
          <DialogTitle>Acquisisci movimenti bancari</DialogTitle>
          <DialogDescription>Esercizio {fiscalYear}. Il file deve contenere un solo anno; viene letto e riconciliato senza reinserire duplicati.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 border-b" data-testid="import-mode-tabs">
          <button type="button" onClick={() => setTab('file')} className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === 'file' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'}`} data-testid="import-file-tab"><FileSpreadsheet className="inline h-4 w-4 mr-1" />File banca</button>
          <button type="button" onClick={() => setTab('manual')} className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === 'manual' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'}`} data-testid="import-manual-tab"><Plus className="inline h-4 w-4 mr-1" />Manuale</button>
        </div>
        {tab === 'file' ? (
          <div className="border border-dashed border-slate-300 rounded-md p-6 bg-slate-50">
            <Label htmlFor="bank-file">Estratto conto CSV, XLS, XLSX, XML/CAMT o PDF PayPal</Label>
            <Input id="bank-file" type="file" accept=".csv,.txt,.xls,.xlsx,.xml,.pdf" className="mt-2 bg-white" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bank-statement-file-input" />
            <p className="mt-2 text-xs text-slate-500">Banche: data, valuta, importo/dare-avere, causale, controparte, IBAN, riferimento e saldo. PayPal PDF: lordo, commissione, netto, stato, valuta e ID/Codice.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Data</Label><Input type="date" value={manual.transaction_date} onChange={(e) => setManual({ ...manual, transaction_date: e.target.value })} data-testid="manual-transaction-date" /></div>
            <div><Label>Tipo</Label><select value={manual.direction} onChange={(e) => setManual({ ...manual, direction: e.target.value })} className="h-9 w-full rounded-md border bg-white px-3 text-sm" data-testid="manual-transaction-direction"><option value="income">Entrata</option><option value="expense">Uscita</option></select></div>
            <div><Label>Importo</Label><Input type="number" min="0.01" step="0.01" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} data-testid="manual-transaction-amount" /></div>
            <div><Label>Controparte</Label><Input value={manual.counterparty_name} onChange={(e) => setManual({ ...manual, counterparty_name: e.target.value })} data-testid="manual-transaction-counterparty" /></div>
            <div className="sm:col-span-2"><Label>Descrizione / causale</Label><Input value={manual.description} onChange={(e) => setManual({ ...manual, description: e.target.value })} data-testid="manual-transaction-description" /></div>
            <div><Label>IBAN controparte</Label><Input value={manual.counterparty_iban} onChange={(e) => setManual({ ...manual, counterparty_iban: e.target.value })} data-testid="manual-transaction-iban" /></div>
            <div><Label>Riferimento/CRO/TRN</Label><Input value={manual.reference} onChange={(e) => setManual({ ...manual, reference: e.target.value })} data-testid="manual-transaction-reference" /></div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="accounting-import-cancel">Annulla</Button>
          <Button type="button" disabled={saving || !companyId || !accountId} onClick={tab === 'file' ? upload : createManual} data-testid="accounting-import-submit">{saving ? 'Elaborazione…' : tab === 'file' ? 'Importa e riconcilia' : 'Registra movimento'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};